import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  /** Lucide icon component, rendered in a muted circle. */
  icon?: LucideIcon;
  title: string;
  description?: string;
  /** Primary call-to-action, e.g. a <Button>. */
  action?: React.ReactNode;
  /** Extra content rendered below the action (e.g. keyboard hints). */
  children?: React.ReactNode;
  className?: string;
  /**
   * `compact` — for an empty state INSIDE a card (a filtered table with no match): the title as muted body
   * text rather than a heading, no icon unless one is passed, tighter padding. `default` is the page-level form.
   */
  size?: 'default' | 'compact';
}

// Canonical empty/idle placeholder. Replaces the per-feature variants
// (SalesOrderDetailEmpty, the inline QuotationDetailPanel EmptyState, etc.).
// Tokenized (bg-muted / text-muted-foreground) so it themes cleanly.
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  children,
  className,
  size = 'default',
}: EmptyStateProps) {
  if (size === 'compact') {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2 px-4 py-10 text-center', className)}>
        {Icon && <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />}
        <p className="text-sm text-muted-foreground">{title}</p>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        {action}
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center gap-3 p-8 text-center',
        className,
      )}
    >
      {Icon && (
        <div className="rounded-full bg-muted p-4">
          <Icon className="h-8 w-8 text-muted-foreground" />
        </div>
      )}
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action && <div className="mt-2">{action}</div>}
      {children}
    </div>
  );
}
